import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  DarkModeIcon,
  LightModeIcon,
  ProfileIcon,
  SettingsIcon,
  SidebarCloseIcon,
  SidebarOpenIcon,
} from "./sidebar-icons";
import { useTheme } from "@/contexts/ThemeContext";
import { useDefaultWorkspace } from "@/hooks/useDefaultWorkspace";
import { useDepartmentStore } from "@/features/department";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  getActiveNavItem,
  getSidebarNavItemsV2,
  isNavSection,
  type NavEntryV2,
  type NavItem,
} from "./nav-items";
import { useSidebarPinned } from "./useSidebarPinned";
import { AppSidebarUserMenu } from "./AppSidebarUserMenu";

const HOVER_ENTER_DELAY_MS = 200;
const HOVER_LEAVE_DELAY_MS = 300;

/**
 * Brand mark — mirrors `MarketingNav.tsx`. Collapsed shows the square only;
 * expanded shows the square plus the `Wiser`+`Studio` wordmark.
 */
function BrandMark({ expanded }: { expanded: boolean }) {
  return (
    <Link
      to="/workspaces"
      aria-label="WiserStudio"
      className={cn(
        "flex items-center ds-title",
        expanded ? "gap-2.5 px-3" : "justify-center w-full",
      )}
    >
      <div className="w-7 h-7 rounded-full bg-foreground flex items-center justify-center shrink-0">
        <div className="w-2.5 h-2.5 rounded-full bg-background" />
      </div>
      {expanded && (
        <span className="leading-none">
          Wiser<span className="text-primary">Studio</span>
        </span>
      )}
    </Link>
  );
}

function CollapsedNavLink({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={item.href}
          onClick={onClick}
          aria-label={item.name}
          data-highlight-id={item.highlightId}
          className={cn(
            "flex items-center justify-center h-10 w-10 rounded-xl transition-all",
            active
              ? "bg-primary/10 text-primary"
              : "text-foreground/60 hover:bg-accent hover:text-foreground",
          )}
        >
          <item.icon className="h-[18px] w-[18px]" />
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right">{item.name}</TooltipContent>
    </Tooltip>
  );
}

function ExpandedNavLink({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      data-highlight-id={item.highlightId}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ds-body-sm-strong",
        active
          ? "bg-primary/10 text-primary"
          : "text-foreground/70 hover:bg-accent hover:text-foreground",
      )}
    >
      <item.icon className="h-[18px] w-[18px] shrink-0" />
      <span className="truncate">{item.name}</span>
    </Link>
  );
}

function ExpandedSubLink({
  href,
  name,
  active,
  onClick,
}: {
  href: string;
  name: string;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      to={href}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 pl-11 pr-3 py-1.5 rounded-lg transition-colors text-[13px]",
        active
          ? "text-primary font-medium"
          : "text-foreground/55 hover:text-foreground hover:bg-accent/60",
      )}
    >
      <span className="truncate">{name}</span>
    </Link>
  );
}

/**
 * Lean app sidebar — replaces the old Maestro orchestrator side panel.
 *
 * Layout: a 64 px collapsed rail keeps its slot in the flex layout. The
 * expanded 240 px panel is rendered as an absolute overlay and slides in via
 * a `transform: translateX(...)` so toggling never reflows the page.
 *
 * Interaction: collapsed by default with hover-to-peek (200 ms enter / 150 ms
 * leave) plus click-to-pin (persisted in `localStorage.wiser_sidebar_pinned`).
 *
 * Hidden on `< md` viewports — the existing `MobileBottomNav` owns nav there.
 */
export function AppSidebar() {
  const { pathname } = useLocation();
  const { defaultWorkspaceSlug } = useDefaultWorkspace();
  const { pinned, togglePinned, setPinned } = useSidebarPinned();
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const navEntries: NavEntryV2[] = getSidebarNavItemsV2(defaultWorkspaceSlug).filter(
    (entry) => isNavSection(entry) || entry.href !== "/workspaces",
  );
  const navItems: NavItem[] = navEntries.filter((entry): entry is NavItem => !isNavSection(entry));

  const [hovered, setHovered] = useState(false);
  const enterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup any pending timers on unmount.
  useEffect(() => {
    return () => {
      if (enterTimer.current) clearTimeout(enterTimer.current);
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, []);

  function handleMouseEnter() {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    if (hovered) return;
    enterTimer.current = setTimeout(() => {
      setHovered(true);
      enterTimer.current = null;
    }, HOVER_ENTER_DELAY_MS);
  }

  // Only cancel the enter timer — never start a close timer here.
  // The expanded panel overflows the aside's 64px box, so mouseleave on the
  // aside fires whenever the cursor moves into that overflow area. Closing on
  // aside-leave would collapse the panel while the cursor is still inside it.
  function handleAsideMouseLeave() {
    if (enterTimer.current) {
      clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
  }

  // Only the expanded panel's leave should trigger collapse.
  function handlePanelMouseLeave() {
    if (enterTimer.current) {
      clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
    leaveTimer.current = setTimeout(() => {
      setHovered(false);
      leaveTimer.current = null;
    }, HOVER_LEAVE_DELAY_MS);
  }

  const isExpanded = pinned || hovered;

  // Departments link should reset the in-flight department workflow state, same
  // as the mobile bottom nav does (`MobileBottomNav.tsx:27`).
  function handleDepartmentsClick(item: NavItem) {
    if (item.href.includes("/departments")) {
      useDepartmentStore.getState().reset();
    }
  }

  return (
    <aside
      data-testid="app-sidebar"
      data-expanded={isExpanded ? "true" : "false"}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleAsideMouseLeave}
      className="hidden md:flex relative h-full w-16 shrink-0 z-40"
    >
      {/* Collapsed rail — always rendered, occupies the 64 px flex slot. */}
      <div className="flex flex-col items-center w-16 py-4 h-full bg-surface-container border-r border-border/50 shrink-0">
        <div className="mb-4">
          <BrandMark expanded={false} />
        </div>

        <nav className="flex flex-col items-center gap-2 flex-1 w-full">
          {(() => {
            const activeItem = getActiveNavItem(pathname, navItems);
            return navItems.map((item) => (
              <CollapsedNavLink
                key={item.href}
                item={item}
                active={activeItem === item}
                onClick={() => handleDepartmentsClick(item)}
              />
            ));
          })()}
        </nav>

        <div className="mt-auto flex flex-col items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                className="flex items-center justify-center h-10 w-10 rounded-xl text-foreground/60 hover:bg-accent hover:text-foreground transition-all"
              >
                {isDark ? (
                  <LightModeIcon className="h-[18px] w-[18px]" />
                ) : (
                  <DarkModeIcon className="h-[18px] w-[18px]" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {isDark ? "Switch to light mode" : "Switch to dark mode"}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/settings/profile"
                aria-label="Settings & Profile"
                className={cn(
                  "flex items-center justify-center h-10 w-10 rounded-xl transition-all",
                  pathname.startsWith("/settings")
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/60 hover:bg-accent hover:text-foreground",
                )}
              >
                <SettingsIcon className="h-[18px] w-[18px]" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">Settings &amp; Profile</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setPinned(true)}
                aria-label="Pin sidebar to access profile menu"
                className="flex items-center justify-center h-10 w-10 rounded-xl text-foreground/60 hover:bg-accent hover:text-foreground transition-all"
              >
                <ProfileIcon className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Profile &amp; sign-out</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Expanded panel — absolute overlay, slides in over the canvas. */}
      {/* onMouseEnter/Leave here cancel spurious aside-leave events that fire */}
      {/* when the cursor moves into the overflow area beyond the 64 px aside. */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handlePanelMouseLeave}
        aria-hidden={!isExpanded}
        className={cn(
          "absolute inset-y-0 left-0 w-60 flex flex-col py-4",
          "bg-surface-container border-r border-border/50 shadow-ambient",
          "transition-transform duration-200 ease-out will-change-transform",
          isExpanded
            ? "translate-x-0 pointer-events-auto"
            : "-translate-x-full pointer-events-none",
        )}
      >
        <div className="flex items-center justify-between px-2 mb-4">
          <BrandMark expanded />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={togglePinned}
                aria-label={pinned ? "Unpin sidebar" : "Pin sidebar open"}
                aria-pressed={pinned}
                className="flex items-center justify-center h-7 w-7 rounded-md text-foreground/50 hover:bg-accent hover:text-foreground transition-colors"
              >
                {pinned ? (
                  <SidebarCloseIcon className="h-4 w-4" />
                ) : (
                  <SidebarOpenIcon className="h-4 w-4" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {pinned ? "Unpin sidebar" : "Pin sidebar open"}
            </TooltipContent>
          </Tooltip>
        </div>

        <nav className="flex flex-col gap-1 px-2 flex-1 overflow-y-auto ds-scrollbar">
          {(() => {
            const activeItem = getActiveNavItem(pathname, navItems);
            return navEntries.map((entry, idx) => {
              if (isNavSection(entry)) {
                return (
                  <div
                    key={`section-${idx}-${entry.header}`}
                    className="px-3 pt-3 pb-1 ds-micro-strong uppercase text-foreground/40"
                  >
                    {entry.header}
                  </div>
                );
              }
              return (
                <div key={entry.href} className="flex flex-col gap-0.5">
                  <ExpandedNavLink
                    item={entry}
                    active={activeItem === entry}
                    onClick={() => handleDepartmentsClick(entry)}
                  />
                  {entry.subItems?.map((sub) => (
                    <ExpandedSubLink
                      key={sub.href}
                      href={sub.href}
                      name={sub.name}
                      active={pathname === sub.href || pathname.startsWith(sub.href + "/")}
                    />
                  ))}
                </div>
              );
            });
          })()}
        </nav>

        <div className="mt-auto flex flex-col gap-1 px-2 pt-2 border-t border-border/50">
          <button
            type="button"
            onClick={toggleTheme}
            className="flex items-center gap-3 px-3 py-2 rounded-xl ds-body-sm-strong text-foreground/70 hover:bg-accent hover:text-foreground transition-colors"
          >
            {isDark ? (
              <LightModeIcon className="h-[18px] w-[18px] shrink-0" />
            ) : (
              <DarkModeIcon className="h-[18px] w-[18px] shrink-0" />
            )}
            <span className="flex-1 text-left">Theme</span>
            <span className="ds-micro text-foreground/40 uppercase">
              {isDark ? "Dark" : "Light"}
            </span>
          </button>

          <Link
            to="/settings/profile"
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ds-body-sm-strong",
              pathname.startsWith("/settings")
                ? "bg-primary/10 text-primary"
                : "text-foreground/70 hover:bg-accent hover:text-foreground",
            )}
          >
            <SettingsIcon className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1 truncate text-left">Settings</span>
          </Link>

          <AppSidebarUserMenu />
        </div>
      </div>
    </aside>
  );
}
